/**
 * 皮肤视觉配置全局缓存（单例）
 *
 * 用途：让分散在 Chat / Channel / AppDetails / ArticleDetails / MyInfo / UserProfile / Shop 等地的
 * ThemeDecorator 组件无需逐处传入 visualConfig，统一从此缓存读取。
 *
 * 数据来源（按优先级）：
 *   1. setConfigs(list) 注入的"覆盖值"（来自 /api/shop/skins 返回的 visualConfig）
 *   2. BUILTIN_SKIN_VISUAL_CONFIGS 内置兜底（12 款经典皮肤，与后端 DEFAULT_SKIN_VISUAL_CONFIGS 一一对应）
 *
 * 这样未登录用户/未访问 Shop 的页面也能看到与原硬编码等价的视觉，管理端修改后页面重新拉取即生效。
 *
 * visualConfig schema:
 *   badge:        { text: string }
 *   avatarFrame:  { gradient: string[], padding: number, animation: 'none'|'spin'|'pulse-ring'|'star-glow'|'pulse-fire'|'thunder-flash'|'ink-breathe'|'firework-glow', animationDuration: number }
 *   bubble:       { bgGradient: string[], borderColor: string, shadowColor: string, textColor: string, darkTextColor: string }
 *   particles:    { preset: 'glow'|'stream'|'petal'|'spark'|'smoke'|'sparkle'|'ember'|'bubble'|'lightning'|'snowflake'|'ink'|'firework',
 *                    count: number, colors: string[], speed: number, size: number, opacity: number }
 */

// 内置兜底配置（与 server/utils/sqliteStorage.js DEFAULT_SKIN_VISUAL_CONFIGS 保持一致）
const BUILTIN_SKIN_VISUAL_CONFIGS = {
  'skin-aurora': {
    badge: { text: '极光' },
    avatarFrame: { gradient: ['#05c46b', '#0fbcf9'], padding: 3, animation: 'spin', animationDuration: 6 },
    bubble: {
      bgGradient: ['rgba(5, 196, 107, 0.18)', 'rgba(15, 188, 249, 0.18)'],
      borderColor: 'rgba(15, 188, 249, 0.45)',
      shadowColor: 'rgba(15, 188, 249, 0.25)',
      textColor: '#03484a',
      darkTextColor: '#b8f5d8'
    },
    particles: { preset: 'glow', count: 4, colors: ['0, 243, 255', '0, 255, 135'], speed: 1, size: 25, opacity: 0.15 }
  },
  'skin-cyber': {
    badge: { text: '霓虹' },
    avatarFrame: { gradient: ['#ff007f', '#7f00ff'], padding: 3, animation: 'pulse-ring', animationDuration: 2.5 },
    bubble: {
      bgGradient: ['rgba(255, 0, 127, 0.20)', 'rgba(127, 0, 255, 0.20)'],
      borderColor: 'rgba(255, 0, 127, 0.55)',
      shadowColor: 'rgba(255, 0, 127, 0.35)',
      textColor: '#2a0a3a',
      darkTextColor: '#ffc8e8'
    },
    particles: { preset: 'stream', count: 6, colors: ['#ff007f', '#00f0ff'], speed: 1, size: 15, opacity: 0.4 }
  },
  'skin-sakura': {
    badge: { text: '樱花' },
    avatarFrame: { gradient: ['#ff758c', '#ff7eb3'], padding: 3, animation: 'none', animationDuration: 0 },
    bubble: {
      bgGradient: ['rgba(255, 117, 140, 0.20)', 'rgba(255, 126, 179, 0.20)'],
      borderColor: 'rgba(255, 117, 140, 0.55)',
      shadowColor: 'rgba(255, 126, 179, 0.3)',
      textColor: '#5a1a2a',
      darkTextColor: '#ffd0d8'
    },
    particles: { preset: 'petal', count: 8, colors: ['255, 183, 197'], speed: 1, size: 4, opacity: 0.75 }
  },
  'skin-starry': {
    badge: { text: '繁星' },
    avatarFrame: { gradient: ['#00c6ff', '#0072ff', '#fdec6e'], padding: 3, animation: 'star-glow', animationDuration: 4 },
    bubble: {
      bgGradient: ['rgba(0, 198, 255, 0.18)', 'rgba(0, 114, 255, 0.18)', 'rgba(253, 236, 110, 0.18)'],
      borderColor: 'rgba(0, 198, 255, 0.55)',
      shadowColor: 'rgba(253, 236, 110, 0.35)',
      textColor: '#0a2a4a',
      darkTextColor: '#c8e0ff'
    },
    particles: { preset: 'spark', count: 12, colors: ['255, 215, 0'], speed: 1, size: 1.5, opacity: 0.6 }
  },
  'skin-abyss': {
    badge: { text: '深渊' },
    avatarFrame: { gradient: ['#0f0c1b', '#302b63', '#24243e'], padding: 3, animation: 'spin', animationDuration: 8 },
    bubble: {
      bgGradient: ['rgba(15, 12, 27, 0.85)', 'rgba(48, 43, 99, 0.85)', 'rgba(36, 36, 62, 0.85)'],
      borderColor: 'rgba(138, 43, 226, 0.55)',
      shadowColor: 'rgba(138, 43, 226, 0.4)',
      textColor: '#e4d5ff',
      darkTextColor: '#e4d5ff'
    },
    particles: { preset: 'smoke', count: 6, colors: ['138, 43, 226'], speed: 1, size: 12, opacity: 0.2 }
  },
  'skin-gold': {
    badge: { text: '黄金' },
    avatarFrame: { gradient: ['#FFD700', '#FFA500'], padding: 3, animation: 'spin', animationDuration: 7 },
    bubble: {
      bgGradient: ['rgba(255, 215, 0, 0.18)', 'rgba(255, 165, 0, 0.18)'],
      borderColor: 'rgba(255, 165, 0, 0.5)',
      shadowColor: 'rgba(255, 165, 0, 0.3)',
      textColor: '#5a3a00',
      darkTextColor: '#ffe9a8'
    },
    particles: { preset: 'sparkle', count: 12, colors: ['255, 215, 0'], speed: 1, size: 1.5, opacity: 0.4 }
  },
  'skin-phoenix': {
    badge: { text: '烈焰' },
    avatarFrame: { gradient: ['#FF4500', '#FFD700'], padding: 3, animation: 'pulse-fire', animationDuration: 3 },
    bubble: {
      bgGradient: ['rgba(255, 69, 0, 0.18)', 'rgba(255, 140, 0, 0.18)'],
      borderColor: 'rgba(255, 69, 0, 0.5)',
      shadowColor: 'rgba(255, 140, 0, 0.35)',
      textColor: '#5a1a00',
      darkTextColor: '#ffc8a0'
    },
    particles: { preset: 'ember', count: 10, colors: ['255, 215, 0', '60, 80, 80'], speed: 1, size: 2, opacity: 1 }
  },
  'skin-ocean': {
    badge: { text: '深海' },
    avatarFrame: { gradient: ['#0077BE', '#00CED1'], padding: 3, animation: 'spin', animationDuration: 9 },
    bubble: {
      bgGradient: ['rgba(0, 119, 190, 0.18)', 'rgba(0, 206, 209, 0.18)'],
      borderColor: 'rgba(0, 206, 209, 0.5)',
      shadowColor: 'rgba(0, 206, 209, 0.3)',
      textColor: '#022a4a',
      darkTextColor: '#b8e0ff'
    },
    particles: { preset: 'bubble', count: 8, colors: ['180, 230, 255', '0, 206, 209', '0, 119, 190'], speed: 1, size: 3, opacity: 0.6 }
  },
  'skin-thunder': {
    badge: { text: '雷霆' },
    avatarFrame: { gradient: ['#6CB4FF', '#FFFFFF', '#B47BFF'], padding: 3, animation: 'thunder-flash', animationDuration: 2 },
    bubble: {
      bgGradient: ['rgba(108, 180, 255, 0.18)', 'rgba(180, 123, 255, 0.18)'],
      borderColor: 'rgba(108, 180, 255, 0.55)',
      shadowColor: 'rgba(108, 180, 255, 0.4)',
      textColor: '#0a1a4a',
      darkTextColor: '#c8dcff'
    },
    particles: { preset: 'lightning', count: 4, colors: ['190, 225, 255'], speed: 1, size: 25, opacity: 1 }
  },
  'skin-snow': {
    badge: { text: '寒雪' },
    avatarFrame: { gradient: ['#E0F7FA', '#B3E5FC', '#81D4FA'], padding: 3, animation: 'spin', animationDuration: 10 },
    bubble: {
      bgGradient: ['rgba(79, 195, 247, 0.18)', 'rgba(179, 229, 252, 0.18)'],
      borderColor: 'rgba(129, 212, 250, 0.5)',
      shadowColor: 'rgba(129, 212, 250, 0.3)',
      textColor: '#022033',
      darkTextColor: '#d0ecff'
    },
    particles: { preset: 'snowflake', count: 8, colors: ['255, 255, 255'], speed: 1, size: 2, opacity: 0.85 }
  },
  'skin-ink': {
    badge: { text: '水墨' },
    avatarFrame: { gradient: ['#1a1a1a', '#4a4a4a', '#888'], padding: 3, animation: 'ink-breathe', animationDuration: 5 },
    bubble: {
      bgGradient: ['rgba(30, 30, 30, 0.15)', 'rgba(80, 80, 80, 0.15)'],
      borderColor: 'rgba(50, 50, 50, 0.5)',
      shadowColor: 'rgba(0, 0, 0, 0.3)',
      textColor: '#1a1a1a',
      darkTextColor: '#d0d0d0'
    },
    particles: { preset: 'ink', count: 6, colors: ['20, 20, 20'], speed: 1, size: 8, opacity: 0.3 }
  },
  'skin-firework': {
    badge: { text: '烟花' },
    avatarFrame: { gradient: ['#FF1493', '#FFD700', '#00FF7F'], padding: 3, animation: 'firework-glow', animationDuration: 2.5 },
    bubble: {
      bgGradient: ['rgba(255, 20, 147, 0.18)', 'rgba(255, 215, 0, 0.18)'],
      borderColor: 'rgba(255, 215, 0, 0.5)',
      shadowColor: 'rgba(255, 20, 147, 0.35)',
      textColor: '#4a0033',
      darkTextColor: '#ffc8e0'
    },
    particles: { preset: 'firework', count: 4, colors: ['255, 215, 0', '255, 69, 0', '255, 20, 147', '0, 255, 127'], speed: 1, size: 1.5, opacity: 1 }
  }
};

// 运行时覆盖（管理端修改后的 visualConfig）
const _overrides = {};

/**
 * 注入皮肤 visualConfig 覆盖数据。
 * @param {Array<{id: string, visualConfig?: object|null}>} list
 */
function setConfigs(list) {
  if (!Array.isArray(list)) return;
  for (const item of list) {
    if (item && item.id) {
      if (item.visualConfig === null || item.visualConfig === undefined) {
        delete _overrides[item.id];
      } else {
        _overrides[item.id] = item.visualConfig;
      }
    }
  }
}

/** 清空所有覆盖配置（用于注销/切换用户） */
function clear() {
  for (const k of Object.keys(_overrides)) delete _overrides[k];
}

/**
 * 获取指定 skinId 的 visualConfig。
 * @param {string} skinId
 * @returns {object|null} visualConfig 或 null（无特效，如 skin-default）
 */
function getConfig(skinId) {
  if (!skinId || skinId === 'skin-default') return null;
  if (_overrides[skinId] !== undefined) return _overrides[skinId] || null;
  return BUILTIN_SKIN_VISUAL_CONFIGS[skinId] || null;
}

/** 获取所有内置皮肤 id 列表（调试/枚举用） */
function getBuiltinSkinIds() {
  return Object.keys(BUILTIN_SKIN_VISUAL_CONFIGS);
}

export default {
  setConfigs,
  clear,
  getConfig,
  getBuiltinSkinIds,
  // 暴露内置配置（管理端编辑器作为预设参考）
  BUILTIN_SKIN_VISUAL_CONFIGS
};
